const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export async function waitForPageReady(minMs = 2500, maxMs = 14000): Promise<void> {
  const started = Date.now();
  await sleep(minMs);

  while (Date.now() - started < maxMs) {
    const root = document.querySelector(".ph-main") ?? document.body;
    const busy = root.querySelector(
      '[aria-busy="true"], .spinner-border, .placeholder-glow, .placeholder',
    );
    if (!busy) break;
    await sleep(350);
  }

  await sleep(900);
}

export function resolveCaptureElement(selector?: string): HTMLElement {
  if (selector) {
    for (const part of selector.split(",").map((s) => s.trim())) {
      const el = document.querySelector(part);
      if (el instanceof HTMLElement) return el;
    }
  }
  const layout = document.querySelector(".ph-layout");
  if (layout instanceof HTMLElement) return layout;
  const main = document.querySelector(".ph-main");
  if (main instanceof HTMLElement) return main;
  const root = document.querySelector(".demo-capture-root");
  if (root instanceof HTMLElement) return root;
  return document.body;
}

export async function captureElementToBlob(element: HTMLElement): Promise<Blob> {
  const { default: html2canvas } = await import("html2canvas");

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: "#f0fdfa",
    windowWidth: Math.max(window.innerWidth, 1280),
    windowHeight: Math.max(window.innerHeight, 720),
    ignoreElements: (node) =>
      node instanceof HTMLElement &&
      (node.classList.contains("demo-capture-ui") ||
        node.dataset.demoCaptureHide === "true"),
  });

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Capture PNG échouée"))),
      "image/png",
      1,
    );
  });
}

type DirectoryHandle = FileSystemDirectoryHandle;

export async function pickCaptureDirectory(): Promise<DirectoryHandle | null> {
  if (typeof window.showDirectoryPicker !== "function") return null;
  try {
    return await window.showDirectoryPicker({ mode: "readwrite" });
  } catch {
    return null;
  }
}

export async function saveCaptureBlob(
  blob: Blob,
  filename: string,
  directory?: DirectoryHandle | null,
): Promise<"directory" | "download"> {
  if (directory) {
    const handle = await directory.getFileHandle(filename, { create: true });
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
    return "directory";
  }

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
  return "download";
}

export function setCaptureRunning(active: boolean): void {
  document.body.classList.toggle("demo-capture-running", active);
  if (active) {
    document.body.dataset.demoCapture = "true";
  } else {
    delete document.body.dataset.demoCapture;
  }
}

export { sleep };
